import React, { memo, useCallback, useState } from "react";
import { ButtonContainer, ButtonWidth, ChargeHeader, Container, StyledText } from "./styles";
import { FullWidthButton } from "@swiggy-private/dls-web-core/dist/components/FullWidthButton";
import SettleMyBillPopup from "./SettleBillPopup";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types";
import { format, isEmpty } from "@swiggy-private/core-ui";
import { useTheme } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider/use-theme";
import { PAYMENT_CART_HEADER } from "@swiggy-private/cart-test-ids";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

interface ICtaItem {
    link: string;
    text: string;
    type: string;
}

interface IFooter {
    coverChargeRemainingSubtext: string;
    coverChargeRemainingText: string;
    coverChargeRemainingTextColor: string;
    coverChargesExhausted: boolean;
    ctaList: ICtaItem[];
}

interface ISettleBill {
    footer: IFooter;
    goBack: () => void;
    onSettleBillPress: () => void;
}

const SettleBillFooter = (props: ISettleBill) => {
    const { footer, goBack, onSettleBillPress } = props;
    const [isVisible, setVisible] = useState(false);
    const theme = useTheme();

    const onPress = useCallback(
        (cta: ICtaItem, index: number) => {
            if (isEmpty(index)) {
                goBack();
                return;
            }
            setVisible(true);
        },
        [goBack],
    );

    const onClose = useCallback(() => {
        setVisible(false);
    }, []);

    const onSettleBill = useCallback(() => {
        onSettleBillPress();
        setVisible(true);
    }, [onSettleBillPress]);

    return (
        <Container>
            <ChargeHeader>
                <StyledText
                    variant="V2.body2.neutral.medium"
                    color={(footer?.coverChargeRemainingTextColor as keyof IColors) || "primary"}
                    data-testid={PAYMENT_CART_HEADER.SETTLE_BILL_FOOTER}
                >
                    {footer?.coverChargeRemainingText}
                </StyledText>
                <StyledText variant="V2.body3.neutral.regular" color="primary">
                    {footer?.coverChargeRemainingSubtext}
                </StyledText>
            </ChargeHeader>
            <ButtonContainer data-testid={PAYMENT_CART_HEADER.SETTLE_BILL_CTA_LIST}>
                {footer?.ctaList?.map((cta: ICtaItem, index: number) => {
                    const isSecondaryButton = cta?.type === "tertiary-low";
                    const backgroundColor = isSecondaryButton
                        ? theme.colors[theme.colorMode].background_Secondary
                        : undefined;
                    const textColor = isSecondaryButton ? "text_Highest_Emphasis" : "background_Primary";
                    return (
                        <ButtonWidth key={index} tabIndex={index}>
                            <FullWidthButton
                                onClick={() => onPress(cta, index)}
                                data-testid={format(PAYMENT_CART_HEADER.FOOTER_BUTTON_, String(index))}
                                style={{ backgroundColor, color: textColor }}
                                variant="positive"
                            >
                                <Text variant="V2.body2.neutral.bold" color={textColor}>
                                    {cta?.text}
                                </Text>
                            </FullWidthButton>
                        </ButtonWidth>
                    );
                })}
            </ButtonContainer>
            {isVisible ? (
                <SettleMyBillPopup isVisible={isVisible} onSettleBillPress={onSettleBill} onClose={onClose} />
            ) : null}
        </Container>
    );
};

export default memo(SettleBillFooter);
